import { formatCurrency, formatNumber, formatString } from "@ciq/shared/utils/format"

/**
 * Represents a column from a datasource with mapping and display configuration.
 * Encapsulates column metadata including display properties, formatting options, and map settings.
 */
export class DatasourceColumn {
  /**
   * Creates a new DatasourceColumn instance.
   *
   * @param {Object} config - Configuration object for the datasource column
   * @param {string} config.id - Unique identifier for the column
   * @param {string} config.label - User-friendly display name for the column
   * @param {string} config.name - Internal name of the column
   * @param {string|null} config.description - Human-readable description of the column
   * @param {string|null} config.formatOption - Formatting option for displaying column values
   * @param {string} config.group - Grouping category for organizing columns
   * @param {string|null} config.symbol - Symbol or icon associated with this column
   * @param {string} config.type - Data type of the column
   * @param {boolean} config.enabled - Whether this column can be used in zone visualizations
   * @param {boolean} config.mapEnabled - Whether this column can be used in map visualizations
   */
  constructor({
    id,
    label,
    name,
    description,
    formatOption,
    group,
    symbol,
    type,
    enabled,
    mapEnabled,
  }) {
    this.id = id
    this.label = label
    this.name = name
    this.description = description
    this.group = group
    this.formatOption = formatOption
    this.type = type
    this.symbol = symbol
    this.enabled = enabled
    this.mapEnabled = mapEnabled
  }

  /**
   * Creates a DatasourceColumn instance from JSON data.
   *
   * @param {Object} json - JSON object containing column configuration data
   * @param {Object} json.column - Raw column object from the datasource
   * @param {string} json.column.id - Unique identifier for the column
   * @param {string} json.column.name - Internal name of the column
   * @param {string} json.column.type - Data type of the column
   * @param {string} json.display_name - User-friendly display name for the column
   * @param {string|null} json.description - Human-readable description of the column
   * @param {string} json.group - Grouping category for organizing columns
   * @param {string|null} json.format_options - Formatting options for displaying column values
   * @param {string|null} json.symbol - Symbol or icon associated with this column
   * @param {boolean} json.enabled - Whether this column can be used in zone visualizations
   * @param {boolean} json.map_enabled - Whether this column can be used in map visualizations
   * @returns {DatasourceColumn} A new DatasourceColumn instance
   */
  static fromJson(json) {
    return new DatasourceColumn({
      id: json.column.id,
      label: json.display_name,
      name: json.column.name,
      description: json.description,
      group: json.group,
      formatOption: json.format_options,
      type: json.column.type,
      symbol: json.symbol,
      enabled: json.enabled,
      mapEnabled: json.map_enabled,
    })
  }

  static fromRecord(record) {
    const data = new DatasourceColumn({})

    for (const [key, value] of Object.entries(record)) {
      data[key] = value
    }

    return data
  }

  isPercentageMetric() {
    return this.formatOption === "PERCENTAGE"
  }

  /**
   * Formats a column value based on the column's type and formatting options.
   *
   * @param {*} value - The raw value to format
   * @returns {string} The formatted value as a display string
   */
  formatValue(value) {
    if (value == null) {
      return "--"
    }

    const convertToString = () => {
      // Handle different formatting options based on column configuration
      switch (this.formatOption) {
        case "PERCENTAGE":
          return `${Math.round(value * 100)}`
        case "CURRENCY":
          return `${formatCurrency(value)}`
        case "INT":
          if (value) {
            if (this.type.includes("int")) {
              value = Math.round(value)
            } else {
              value = Math.round(value * 100) / 100
            }
          }
          return formatNumber(value)
        default:
          return formatString(value)
      }
    }

    let formattedValue = convertToString()
    if (this.symbol) {
      formattedValue = `${formattedValue}${this.symbol}`
    }

    return formattedValue
  }

  normalizeValue(value) {
    if (value == null) {
      return null
    }

    if (typeof value === "number" && !Number.isInteger(value)) {
      return Math.round(value * 100) / 100
    }

    return value
  }
}
